import axios from 'axios';
import { API_URL } from '../config/apiConfig';

const CACHE_TIME = 15 * 60 * 1000;
const MAX_RATE_AGE = 7 * 24 * 60 * 60 * 1000;

export const isValidUsdRate = (value, now = Date.now()) => {
  const asOf = Date.parse(value?.asOf);
  return value?.base === 'USD' && value?.quote === 'ILS'
    && typeof value.rate === 'number' && Number.isFinite(value.rate) && value.rate > 0
    && Number.isFinite(asOf) && asOf <= now + 5 * 60 * 1000 && now - asOf <= MAX_RATE_AGE;
};

export const approximateUsd = (ils, value) => {
  if (typeof ils !== 'number' || !Number.isFinite(ils) || ils <= 0 || !isValidUsdRate(value)) return null;
  const dollars = Math.round(Math.round(ils) / value.rate);
  return Number.isFinite(dollars) ? dollars : null;
};

export function createExchangeRateClient({ request, now = Date.now }) {
  let cached = null;
  let nextCheck = 0;
  let pending = null;
  return async function getRate() {
    if (pending) return pending;
    if (now() < nextCheck) return isValidUsdRate(cached, now()) ? cached : null;
    pending = (async () => {
      try {
        const data = await request();
        if (!isValidUsdRate(data, now())) throw new Error('INVALID_RATE');
        cached = data;
        nextCheck = now() + CACHE_TIME;
      } catch {
        nextCheck = now() + 60000;
      }
      return isValidUsdRate(cached, now()) ? cached : null;
    })();
    try { return await pending; } finally { pending = null; }
  };
}

export const getUsdRate = createExchangeRateClient({ request: async () => {
  const local = ['localhost', '127.0.0.1'].includes(window.location.hostname);
  const api = (local && process.env.REACT_APP_EXCHANGE_RATE_API_URL) || API_URL;
  return (await axios.get(`${api}/api/public-site/exchange-rate/usd`, { timeout: 7000 })).data;
} });

// Both rates arrive in one source request; validate each currency independently.
export const rateForCurrency = (snapshot, currency) => {
  if (!['USD', 'EUR'].includes(currency)) return null;
  const value = currency === 'EUR' ? snapshot?.eur : snapshot;
  return value?.base === currency && isValidUsdRate({ ...value, base: 'USD' }) ? value : null;
};
export const convertFromIls = (amount, snapshot, currency) => {
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) return null;
  if (currency === 'ILS') return Math.round(amount);
  const rate = rateForCurrency(snapshot, currency);
  const converted = rate ? Math.round(Math.round(amount) / rate.rate) : null;
  return Number.isFinite(converted) ? converted : null;
};
